import { PlusOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { type SelectProps, ConfigProvider, Divider, Modal } from 'antd';
import { type MouseEvent, type ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useGetCollaboratorsQuery } from 'types/atlas-graphql';
import { notEmpty } from 'utils/types';

import { AddCollabContainer, CollaboratorSelect } from './CollaboratorChooser.style';
import { formatCollaborator, groupCollaborators, sortCollaborators } from './utils';

export interface CollaboratorChooserProps extends SelectProps<string[]> {
  // This prop somehow works but could not be found on downstream interface
  hidden?: boolean;
}

export const CollaboratorChooser = ({
  value,
  defaultValue,
  onOpenChange,
  ...props
}: CollaboratorChooserProps) => {
  const { t } = useTranslation();
  const [addModalVisible, setAddModalVisible] = useState(false);
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');

  const { data: collaboratorsData, loading: collaboratorsLoading } = useGetCollaboratorsQuery();
  const collaborators = sortCollaborators(collaboratorsData?.collaborators?.filter(notEmpty) ?? []);

  const sortEmails = (emails: string[]) =>
    sortCollaborators(collaborators.filter(c => emails.includes(c.email))).map(c => c.email);
  const sortedValue = value?.length ? sortEmails(value) : undefined;
  const sortedDefaultValue = defaultValue?.length ? sortEmails(defaultValue) : undefined;

  const valueProps = sortedValue?.length
    ? { value: sortedValue }
    : { defaultValue: sortedDefaultValue };

  const handleAddClick = (_: MouseEvent) => {
    // small wait to let the antd dropdown animations get outta the way
    setTimeout(() => setAddModalVisible(true), 100);
  };

  const handleAddClose = () => {
    setAddModalVisible(false);
  };

  const emptyRender = () => (
    <div>
      <UsergroupAddOutlined style={{ marginRight: '4px' }} />
      <span>{t('workOrder.noCurrentCollaborators', 'No current collaborators')}</span>
    </div>
  );

  // have to use onMouseDown rather than onClick cause antd
  const popupRender = (dropdown: ReactElement) => (
    <div id="collaborator-chooser-dropdown">
      <div onMouseDown={handleAddClick}>
        <AddCollabContainer>
          <PlusOutlined style={{ marginRight: '4px' }} />
          <span>{t('components.addCollaborator', 'Add Collaborator')}</span>
        </AddCollabContainer>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      {dropdown}
    </div>
  );

  const isInitialLoading = collaboratorsLoading && !collaboratorsData;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    onOpenChange?.(open);
  };

  return (
    <ConfigProvider renderEmpty={emptyRender}>
      <Modal
        title={t('components.addACollaborator', 'Add a Collaborator')}
        open={addModalVisible}
        onCancel={handleAddClose}
        footer={null}
      >
        <Trans
          i18nKey="components.collaboratorSupportRequest"
          t={t}
          defaults="Please contact <supportEmail>support@skyspecs.com</supportEmail> to link a new collaborator to your account. Once a collaborator relationship has been established, you will be able to grant that collaborator account access to whichever Work Orders you'd like."
          components={{
            // content is injected by Trans at runtime
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            supportEmail: <a href="mailto:support@skyspecs.com" />,
          }}
        />
      </Modal>
      {!isInitialLoading ? (
        <CollaboratorSelect
          mode="multiple"
          popupMatchSelectWidth={false}
          placeholder={t('components.addCollaboratorsPlaceholder', 'Add Collaborators...')}
          popupRender={popupRender}
          filterOption={(input, option) => {
            return (
              !option?.options &&
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            );
          }}
          {...valueProps}
          {...props}
          autoClearSearchValue={false}
          searchValue={searchValue}
          onSearch={setSearchValue}
          onOpenChange={handleOpenChange}
          options={groupCollaborators(collaborators).map(({ orgName, collaborators: group }) => ({
            label: orgName,
            options: group.map(collaborator => ({
              value: collaborator.email,
              label: formatCollaborator(collaborator),
            })),
          }))}
        />
      ) : null}
    </ConfigProvider>
  );
};
